import type { Database } from '../../types/database.types';

/**
 * Sorting and bucketing for the Sales Tasks page.
 *
 * Pure functions, no query, no component. The page has one job that is easy to
 * get subtly wrong — putting the right thing at the top — and that job is
 * worth testing without mounting anything.
 */

export type SalesTaskRow = Database['public']['Views']['sales_tasks_home']['Row'];

/**
 * The view's columns are all nullable because Postgres cannot promise a view's
 * shape, so this is the same row with the four things the page cannot render
 * without. Rows missing any of them are dropped by `usableRows` rather than
 * rendered half-drawn.
 */
export interface SalesTask extends SalesTaskRow {
  id: string;
  title: string;
  is_completed: boolean;
  record_type: RecordType;
}

export type RecordType = 'deal' | 'lead' | 'prospect' | 'company';

const RECORD_TYPES: RecordType[] = ['deal', 'lead', 'prospect', 'company'];

function isRecordType(value: string | null): value is RecordType {
  return value !== null && (RECORD_TYPES as string[]).includes(value);
}

/**
 * Every row the page can actually draw.
 *
 * A view column typed `string | null` is not a promise that nulls arrive; it
 * is Postgres declining to promise they do not. In practice `id`, `title` and
 * `record_type` are non-null for every row the view can produce. Dropping the
 * impossible row rather than rendering `undefined` costs one filter and means
 * no screen ever shows a task with no name.
 */
export function usableRows(rows: SalesTaskRow[]): SalesTask[] {
  return rows.filter(
    (row): row is SalesTask =>
      typeof row.id === 'string' &&
      typeof row.title === 'string' &&
      typeof row.is_completed === 'boolean' &&
      isRecordType(row.record_type),
  );
}

/** The tabs, in the order the screenshots show them. */
export type SalesTaskTab = 'all' | 'today' | 'overdue' | 'upcoming';

export const SALES_TASK_TABS: { key: SalesTaskTab; label: string }[] = [
  { key: 'all', label: 'All tasks' },
  { key: 'today', label: 'Due today' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'upcoming', label: 'Upcoming' },
];

/**
 * Today as a calendar day in the reader's own timezone.
 *
 * `new Date().toISOString()` is UTC, which in Manila is yesterday for the
 * first eight hours of every day. A task due today would read as overdue
 * before lunch, which is the kind of bug people stop trusting a page over.
 */
export function localIsoDate(now: Date): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export type ReschedulePreset = 'tomorrow' | 'week' | 'month';

/**
 * A quick-reschedule choice as a local calendar date.
 *
 * These are deliberately measured from today, not from the task's old due
 * date: the action answers "when should I do this now?". Month arithmetic is
 * clamped rather than allowed to roll over, so January 31 becomes February's
 * last day instead of an unexpected date in March.
 */
export function presetDueDate(today: Date, preset: ReschedulePreset): string {
  const next = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  if (preset === 'tomorrow') {
    next.setDate(next.getDate() + 1);
  } else if (preset === 'week') {
    next.setDate(next.getDate() + 7);
  } else {
    const wantedDay = next.getDate();
    next.setDate(1);
    next.setMonth(next.getMonth() + 1);
    const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0).getDate();
    next.setDate(Math.min(wantedDay, lastDay));
  }

  return localIsoDate(next);
}

export function isOverdue(task: SalesTask, today: string): boolean {
  // A completed task is never overdue, whatever its date says. Being late is
  // a property of work still owed.
  if (task.is_completed) {
    return false;
  }
  return task.due_date !== null && task.due_date < today;
}

/**
 * Which tab a task belongs to.
 *
 * Completed tasks answer `null` for every tab but 'all'. They are not overdue,
 * they are not due today, and they are certainly not upcoming — and the page
 * puts them in their own fold rather than in any of these.
 */
export function inTab(task: SalesTask, tab: SalesTaskTab, today: string): boolean {
  if (tab === 'all') {
    return true;
  }
  if (task.is_completed || task.due_date === null) {
    return false;
  }
  if (tab === 'overdue') {
    return task.due_date < today;
  }
  if (tab === 'today') {
    return task.due_date === today;
  }
  return task.due_date > today;
}

/**
 * Soonest first, furthest last.
 *
 * Kyu: "Auto filter most recent due on top to far from now due at the bottom."
 *
 * Overdue sorts above today, which falls out of comparing dates and is also
 * what you want: three weeks late is more urgent than due this afternoon, not
 * less.
 *
 * An undated task sorts LAST, not first. A task with no due date is not
 * urgent, it is unscheduled, and putting it above work that is actually late
 * would bury the thing the page exists to surface. Ties break on title so the
 * order is stable between renders rather than shuffling on every refetch.
 */
export function sortByDue(tasks: SalesTask[]): SalesTask[] {
  return [...tasks].sort((left, right) => {
    if (left.due_date === null && right.due_date === null) {
      return left.title.localeCompare(right.title);
    }
    if (left.due_date === null) {
      return 1;
    }
    if (right.due_date === null) {
      return -1;
    }
    if (left.due_date !== right.due_date) {
      return left.due_date < right.due_date ? -1 : 1;
    }
    return left.title.localeCompare(right.title);
  });
}

export interface SalesTaskBoard {
  /** Open tasks in the chosen tab, soonest due first. */
  open: SalesTask[];
  /** Completed tasks, newest completion first. Its own fold at the bottom. */
  done: SalesTask[];
  /** How many open tasks are late. Drives the count beside the Overdue tab. */
  overdueCount: number;
}

/**
 * Everything the page draws, worked out in one place.
 *
 * Completed tasks are split out BEFORE the tab filter rather than after: the
 * fold at the bottom holds everything finished regardless of which tab is
 * showing, because "what did we get done" is not a question about due dates.
 */
export function buildBoard(rows: SalesTaskRow[], tab: SalesTaskTab, today: string): SalesTaskBoard {
  const usable = usableRows(rows);
  const open = usable.filter((task) => !task.is_completed);
  const done = usable.filter((task) => task.is_completed);

  return {
    open: sortByDue(open.filter((task) => inTab(task, tab, today))),
    done: [...done].sort((left, right) => {
      const leftDone = left.completed_at ?? '';
      const rightDone = right.completed_at ?? '';
      if (leftDone !== rightDone) {
        return leftDone < rightDone ? 1 : -1;
      }
      return left.title.localeCompare(right.title);
    }),
    overdueCount: open.filter((task) => isOverdue(task, today)).length,
  };
}

/**
 * Where the About column goes when you click it.
 *
 * ── The bug this comment used to be ────────────────────────────────────────
 *
 * The first version returned a bare `/deals` and said, in a comment, "No
 * per-deal address exists yet." That was not true. `/deals?deal=<id>` was
 * built for the Inbox weeks earlier, and DealsMonitoringView still carries the
 * reason in its own comment: "without it a notification lands somebody on a
 * board and leaves them to find the deal it was already about."
 *
 * Nobody checked. Kyu did, by clicking: "I click the follow up and code verify
 * fix task and it just leads me straight to sales record, CG deals. This is
 * messed up." Almost every row on the page is a DEAL, so the one type that was
 * wrong was the one that mattered.
 *
 * The lesson is in the test below rather than in this paragraph: every record
 * type that HAS an id-addressable route must put the id in the href, and the
 * test asserts that by looking for the id rather than for a string I typed.
 *
 * ── The four addresses, each read from the screen that serves it ───────────
 *
 *   deal     /deals?deal=<id>                        DealsMonitoringView
 *   lead     /tracker?lead=<id>                      TrackerPage
 *   company  /tracker?tab=companies&company=<id>     TrackerPage
 *   prospect /tracker?tab=linkedin                   no per-person route
 *
 * Prospecting genuinely has no per-record address: nothing anywhere reads a
 * `prospect` search param. That one is a screen link honestly, not by mistake.
 */
export function recordHref(task: SalesTask): string {
  if (task.deal_id !== null && task.record_type === 'deal') {
    return `/deals?deal=${task.deal_id}`;
  }
  if (task.lead_id !== null && task.record_type === 'lead') {
    return `/tracker?lead=${task.lead_id}`;
  }
  if (task.record_type === 'prospect') {
    return '/tracker?tab=linkedin';
  }
  if (task.company_id !== null) {
    return `/tracker?tab=companies&company=${task.company_id}`;
  }
  return '/tracker';
}

/** What the row says this task is about, when the record has no name. */
export const RECORD_TYPE_LABELS: Record<RecordType, string> = {
  deal: 'Deal',
  lead: 'Lead',
  prospect: 'Prospect',
  company: 'Company',
};

export const TASK_TYPE_LABELS: Record<Database['public']['Enums']['task_type'], string> = {
  todo: 'To-do',
  call: 'Call',
  email: 'Email',
  linkedin: 'LinkedIn',
};
